import { useState, useEffect, useCallback } from "react";
import { Select, Input, Tabs, Button, message, Spin, Card, Avatar } from "antd";
import { CheckOutlined, EnvironmentOutlined, DeleteOutlined } from "@ant-design/icons";
import { useGetTripMediaList, useApproveTripMedia, useDeleteTripMedia } from "../services/tripmedia.service";
import { useFetchCities } from "../services/cities.service";
import { useAuth } from "../context/AuthContext";
import { storage } from "../utils/storage";
import { UseDebounce } from "../helpers/common";
import { ITripMediaItem } from "../types/tripmedia";
import { useGetSasToken } from "../services/blobcontext.service";
import IResponse from "../types/response";
import { useNavigate } from "react-router-dom";

const { Option } = Select;
const { TabPane } = Tabs;
const { Search } = Input;

const TripMedia = () => {
    const { hasPermission, permissionsLoaded, loadPermissions } = useAuth();
    const navigate = useNavigate();

    const [tripMediaList, setTripMediaList] = useState<ITripMediaItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    // Filters state
    const [activeTab, setActiveTab] = useState<string>("all");
    const [searchFilter, setSearchFilter] = useState<string>("");
    const debouncedSearchFilter = UseDebounce(searchFilter, 500);
    const [contentType, setContentType] = useState<string>("");
    const [cityID, setCityID] = useState<string | null>(null);

    const [cities, setCities] = useState<any[]>([]);
    const [sasToken, setSasToken] = useState<string | null>(null);
    const [isSasLoaded, SetIsSasLoaded] = useState<boolean>(false);

    // Modal state
    const [selectedItem, setSelectedItem] = useState<ITripMediaItem | null>(null);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [modalMediaIndex, setModalMediaIndex] = useState<number>(0);

    useEffect(() => {
        loadPermissions();
    }, [permissionsLoaded]);

    const TripMediaEdit = hasPermission("TRIP_MEDIA", "UPDATE");
    const TripMediaDelete = hasPermission("TRIP_MEDIA", "DELETE");

    const fetchTripMediaMutation = useGetTripMediaList({
        onSuccess: (res: any) => {
            setLoading(false);
            if (res.statusCode === 200 && res.data) {
                const formattedData = res.data.map((data: any) => {
                    const featuredImageUrl = data.featuredImageUrl
                        ? `${data.featuredImageUrl}?${sasToken}`
                        : null;

                    const tripFiles = data.tripMediaFilesDto.map((file: any) => {
                        return {
                            filePath: `${file.filePath}?${sasToken}`,
                            fileType: file.fileType,
                        }
                    });

                    data.tripMediaFilesDto = tripFiles;

                    return {
                        ...data,
                        featuredImageUrl,
                        tripMediaFilesDto: tripFiles,
                    };
                });
                setTripMediaList(formattedData);
            } else {
                setTripMediaList([]);
            }
        },
        onError: (err: any) => {
            setLoading(false);
            message.error(err?.message || "Failed to fetch trip media.");
        },
    });

    const approveMediaMutation = useApproveTripMedia({
        onSuccess: (res: any) => {
            if (res.statusCode === 200) {
                message.success("Media status updated successfully.");
                fetchList();
            } else {
                message.error(res?.message || "Failed to update media status.");
            }
        },
        onError: (err: any) => {
            message.error(err?.message || "Error updating media status.");
        },
    });

    const getSasTokenMutation = useGetSasToken({
        onSuccess: (response: IResponse) => {
            if (response?.data != null) {
                setSasToken(response.data);
                SetIsSasLoaded(true);
                message.success(response.statusMessage);
                return;
            }
            message.error(response.statusMessage);
            SetIsSasLoaded(true);
        },
        onError: (error) => {
            message.error(error.message);
            SetIsSasLoaded(true);
        },
    });

    const deleteMediaMutation = useDeleteTripMedia({
        onSuccess: (res: any) => {
            if (res.statusCode === 200) {
                message.success("Media deleted successfully.");
                fetchList();
            } else {
                message.error(res?.message || "Failed to delete media.");
            }
        },
        onError: (err: any) => {
            message.error(err?.message || "Error deleting media.");
        },
    });

    const fetchCitiesMutation = useFetchCities({
        onSuccess: (res: any) => {
            if (res.statusCode === 200 && res.data) {
                setCities(
                    res.data.map((data: any) => ({
                        value: data.cityId,
                        label: data.cityName,
                    }))
                );
            }
        },
    });

    const getSasToken = () => getSasTokenMutation.mutate({});

    useEffect(() => {
        fetchCitiesMutation.mutate({ searchFilter: "", countryId: storage.getCountryId() });
    }, []);

    const fetchList = useCallback(() => {
        setLoading(true);
        fetchTripMediaMutation.mutate({
            searchFilter: debouncedSearchFilter || null,
            contentType: contentType || null,
            isApproved: activeTab === "approved" ? true : null,
            cityID: cityID || null,
        });
    }, [debouncedSearchFilter, contentType, activeTab, cityID]);

    useEffect(() => {
        if (isSasLoaded) fetchList();
    }, [isSasLoaded, activeTab, cityID, contentType]);

    useEffect(() => {
        const userRoleId = parseInt(storage.getUserRoleId() ?? "0");
        if (![1, 4].includes(userRoleId)) {
            navigate("/");
        } else {
            if (!sasToken) getSasToken();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleApprove = (id: string, isApproved: boolean) => {
        approveMediaMutation.mutate({ tripMediaID: id, isApproved });
    };

    const handleDelete = (id: string) => {
        if (window.confirm("Are you sure you want to delete this media?")) {
            deleteMediaMutation.mutate(id);
        }
    };

    const openModal = (item: ITripMediaItem) => {
        setSelectedItem(item);
        setModalMediaIndex(0);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedItem(null);
        setModalMediaIndex(0);
    };

    const handleDownload = async (url: string, filename: string) => {
        try {
            const res = await fetch(url);
            const blob = await res.blob();
            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(blobUrl);
        } catch {
            window.open(url, "_blank");
        }
    };

    /** Returns deduplicated list: featured image first, then extra files. */
    const getModalMediaFiles = (item: ITripMediaItem) => {
        const files: { filePath: string; fileType: string; label: string }[] = [];
        if (item.featuredImageUrl) {
            files.push({ filePath: item.featuredImageUrl, fileType: "image", label: "Featured Image" });
        }
        item.tripMediaFilesDto?.forEach((f, idx) => {
            const isDuplicate = item.featuredImageUrl && f.filePath === item.featuredImageUrl;
            if (!isDuplicate) {
                files.push({ filePath: f.filePath, fileType: f.fileType, label: `Media ${idx + 1}` });
            }
        });
        return files;
    };

    return (
        <div className="w-full">
            {/* ── Page Header ── */}
            <div className="flex justify-between items-start mb-6">
                <div>
                    <h1 className="text-2xl font-bold mb-1">Content Submissions</h1>
                    <p className="text-gray-500 text-sm">
                        Gathered photos and videos from travelers and attendants for social media posts
                    </p>
                </div>
                <div className="w-80 max-w-80">
                    <Search
                        placeholder="Search by user, caption, or location"
                        size="large"
                        enterButton
                        className="search-field"
                        value={searchFilter}
                        onChange={(e) => setSearchFilter(e.target.value)}
                    />
                </div>
            </div>

            {/* ── Tabs ── */}
            <Tabs
                activeKey={activeTab}
                onChange={(key) => setActiveTab(key)}
                className="mb-4 font-semibold"
                tabBarGutter={32}
            >
                <TabPane tab="All Submissions" key="all" />
                <TabPane tab="Approved" key="approved" />
            </Tabs>

            {/* ── Filters ── */}
            <div className="flex flex-wrap items-center gap-4 mb-8 bg-gray-50 p-4 rounded-lg">
                <Select
                    placeholder="All Content"
                    value={contentType || ""}
                    onChange={(val) => setContentType(val)}
                    className="w-48"
                >
                    <Option value="">All Content</Option>
                    <Option value="image">Image</Option>
                    <Option value="video">Video</Option>
                </Select>

                <Select
                    showSearch
                    className="w-48 font-medium text-neutral-600"
                    style={{ minWidth: 192 }}
                    placeholder="Please select a City"
                    optionFilterProp="label"
                    options={cities}
                    onChange={(value) => setCityID(value)}
                    allowClear
                />
            </div>

            {/* ── Card Grid ── */}
            <Spin spinning={loading}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {tripMediaList.map((item) => {
                        let imageUrl = item.featuredImageUrl;
                        if (!imageUrl && item.tripMediaFilesDto?.length > 0) {
                            imageUrl = item.tripMediaFilesDto[0].filePath;
                        }

                        return (
                            <Card
                                key={item.tripMediaID}
                                hoverable
                                bodyStyle={{ padding: 0 }}
                                className="rounded-xl overflow-hidden shadow-sm border border-gray-200"
                                style={{ cursor: "pointer" }}
                                onClick={() => openModal(item)}
                            >
                                {/* Thumbnail */}
                                <div className="h-48 w-full bg-gray-100 flex items-center justify-center relative overflow-hidden">
                                    {imageUrl ? (
                                        <img
                                            src={imageUrl}
                                            alt={item.caption}
                                            className="object-cover w-full h-full"
                                        />
                                    ) : (
                                        <div className="text-gray-400">No Image</div>
                                    )}
                                </div>

                                {/* Card Body */}
                                <div className="p-4 relative mt-[-20px] bg-white pt-6">
                                    <Avatar
                                        size={48}
                                        className="absolute -top-6 left-4 border-2 border-white shadow-sm"
                                        style={{ backgroundColor: "black" }}
                                    >
                                        {item.agentName
                                            ? item.agentName.charAt(0)
                                            : item.travellerName?.charAt(0) || "U"}
                                    </Avatar>

                                    <div className="ml-16">
                                        <h3 className="font-bold text-base m-0 leading-tight truncate">
                                            {item.agentName || item.travellerName || "Unknown User"}
                                        </h3>
                                    </div>

                                    <div className="flex items-center text-gray-500 mt-3 text-sm">
                                        <EnvironmentOutlined className="mr-1" />
                                        <span className="truncate">{item.cityName || "Unknown Location"}</span>
                                    </div>

                                    {/* Stop card-click from propagating when using action buttons */}
                                    <div
                                        className="mt-4 flex justify-end space-x-2"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {!item.isApproved && TripMediaEdit && (
                                            <Button
                                                type="primary"
                                                style={{
                                                    backgroundColor: "black",
                                                    color: "white",
                                                    borderColor: "black",
                                                }}
                                                className="flex items-center rounded-md font-medium hover:opacity-80"
                                                onClick={() => handleApprove(item.tripMediaID, true)}
                                                icon={<CheckOutlined />}
                                            >
                                                Approve
                                            </Button>
                                        )}
                                        {item.isApproved && (
                                            <span className="text-green-600 font-medium flex items-center text-sm bg-green-50 px-3 py-1 rounded-full mr-auto">
                                                <CheckOutlined className="mr-1" /> Approved
                                            </span>
                                        )}
                                        {TripMediaDelete && (
                                            <Button
                                                danger
                                                type="text"
                                                className="flex items-center rounded-md font-medium"
                                                onClick={() => handleDelete(item.tripMediaID)}
                                                icon={<DeleteOutlined />}
                                            />
                                        )
                                        }

                                    </div>
                                </div>
                            </Card>
                        );
                    })}
                </div>

                {!loading && tripMediaList.length === 0 && (
                    <div className="w-full text-center py-16 text-gray-500">
                        No submissions found matching the criteria.
                    </div>
                )}
            </Spin>

            {/* ════════════════════════════════════════
                Media Preview Modal (custom, no Ant Modal)
                ════════════════════════════════════════ */}
            {isModalOpen && selectedItem && (() => {
                const mediaFiles = getModalMediaFiles(selectedItem);
                const current = mediaFiles[modalMediaIndex];
                const userName =
                    selectedItem.agentName || selectedItem.travellerName || "Unknown User";
                const initials = userName.charAt(0).toUpperCase();

                return (
                    /* Backdrop */
                    <div
                        style={{
                            position: "fixed",
                            inset: 0,
                            zIndex: 1050,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "rgba(0,0,0,0.78)",
                        }}
                        onClick={closeModal}
                    >
                        {/* Modal card */}
                        <div
                            style={{
                                background: "#fff",
                                borderRadius: 16,
                                width: "90%",
                                maxWidth: 800,
                                maxHeight: "92vh",
                                overflowY: "auto",
                                boxShadow: "0 24px 64px rgba(0,0,0,0.45)",
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* ── Modal Header ── */}
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    padding: "16px 20px",
                                    borderBottom: "1px solid #f0f0f0",
                                }}
                            >
                                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                    <div
                                        style={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: "50%",
                                            background: "#3b82f6",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: "#fff",
                                            fontWeight: 700,
                                            fontSize: 16,
                                            flexShrink: 0,
                                        }}
                                    >
                                        {initials}
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 700, fontSize: 15 }}>{userName}</div>
                                        <div
                                            style={{
                                                color: "#6b7280",
                                                fontSize: 13,
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 4,
                                            }}
                                        >
                                            <EnvironmentOutlined style={{ fontSize: 12 }} />
                                            {selectedItem.cityName || "Unknown Location"}
                                        </div>
                                    </div>
                                </div>

                                <button
                                    onClick={closeModal}
                                    style={{
                                        background: "none",
                                        border: "none",
                                        cursor: "pointer",
                                        fontSize: 22,
                                        color: "#6b7280",
                                        lineHeight: 1,
                                        padding: "4px 8px",
                                    }}
                                    aria-label="Close modal"
                                >
                                    ✕
                                </button>
                            </div>

                            {/* ── Caption ── */}
                            {selectedItem.caption && (
                                <div
                                    style={{
                                        padding: "10px 20px",
                                        fontSize: 14,
                                        color: "#374151",
                                        borderBottom: "1px solid #f0f0f0",
                                    }}
                                >
                                    {selectedItem.caption}
                                </div>
                            )}

                            {/* ── Main Viewer ── */}
                            {current ? (
                                <div
                                    style={{
                                        background: "#111",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        minHeight: 340,
                                        position: "relative",
                                    }}
                                >
                                    {["mp4", "mov", "avi", "webm", "video"].includes(
                                        current.fileType?.toLowerCase()
                                    ) ? (
                                        <video
                                            key={current.filePath}
                                            src={current.filePath}
                                            controls
                                            style={{ maxWidth: "100%", maxHeight: 440, display: "block" }}
                                        />
                                    ) : (
                                        <img
                                            key={current.filePath}
                                            src={current.filePath}
                                            alt={current.label}
                                            style={{
                                                maxWidth: "100%",
                                                maxHeight: 440,
                                                objectFit: "contain",
                                                display: "block",
                                            }}
                                        />
                                    )}

                                    {/* Prev / Next arrows */}
                                    {mediaFiles.length > 1 && (
                                        <>
                                            <button
                                                onClick={() =>
                                                    setModalMediaIndex((i) => Math.max(0, i - 1))
                                                }
                                                disabled={modalMediaIndex === 0}
                                                style={{
                                                    position: "absolute",
                                                    left: 10,
                                                    top: "50%",
                                                    transform: "translateY(-50%)",
                                                    background: "rgba(255,255,255,0.18)",
                                                    border: "none",
                                                    borderRadius: "50%",
                                                    width: 38,
                                                    height: 38,
                                                    color: "#fff",
                                                    fontSize: 20,
                                                    cursor: modalMediaIndex === 0 ? "not-allowed" : "pointer",
                                                    opacity: modalMediaIndex === 0 ? 0.35 : 1,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                ‹
                                            </button>
                                            <button
                                                onClick={() =>
                                                    setModalMediaIndex((i) =>
                                                        Math.min(mediaFiles.length - 1, i + 1)
                                                    )
                                                }
                                                disabled={modalMediaIndex === mediaFiles.length - 1}
                                                style={{
                                                    position: "absolute",
                                                    right: 10,
                                                    top: "50%",
                                                    transform: "translateY(-50%)",
                                                    background: "rgba(255,255,255,0.18)",
                                                    border: "none",
                                                    borderRadius: "50%",
                                                    width: 38,
                                                    height: 38,
                                                    color: "#fff",
                                                    fontSize: 20,
                                                    cursor:
                                                        modalMediaIndex === mediaFiles.length - 1
                                                            ? "not-allowed"
                                                            : "pointer",
                                                    opacity:
                                                        modalMediaIndex === mediaFiles.length - 1 ? 0.35 : 1,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                ›
                                            </button>
                                        </>
                                    )}
                                </div>
                            ) : (
                                <div style={{ padding: 48, textAlign: "center", color: "#9ca3af" }}>
                                    No media available
                                </div>
                            )}

                            {/* ── Download bar ── */}
                            {current && (
                                <div
                                    style={{
                                        padding: "12px 20px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        borderBottom:
                                            mediaFiles.length > 1 ? "1px solid #f0f0f0" : "none",
                                    }}
                                >
                                    <span style={{ fontSize: 13, color: "#6b7280" }}>
                                        {current.label}&nbsp;·&nbsp;
                                        {current.fileType.charAt(0).toUpperCase() +
                                            current.fileType.slice(1)}
                                        {mediaFiles.length > 1 &&
                                            ` (${modalMediaIndex + 1} / ${mediaFiles.length})`}
                                    </span>
                                    <button
                                        onClick={() => {
                                            const ext =
                                                current.filePath.split("?")[0].split(".").pop() ||
                                                current.fileType;
                                            handleDownload(
                                                current.filePath,
                                                `${selectedItem.caption || "media"}_${modalMediaIndex + 1}.${ext}`
                                            );
                                        }}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 6,
                                            background: "#111",
                                            color: "#fff",
                                            border: "none",
                                            borderRadius: 8,
                                            padding: "7px 18px",
                                            cursor: "pointer",
                                            fontSize: 13,
                                            fontWeight: 600,
                                        }}
                                    >
                                        ⬇&nbsp;Download
                                    </button>
                                </div>
                            )}

                            {/* ── Thumbnail strip ── */}
                            {mediaFiles.length > 1 && (
                                <div
                                    style={{
                                        display: "flex",
                                        gap: 8,
                                        padding: "12px 20px",
                                        overflowX: "auto",
                                    }}
                                >
                                    {mediaFiles.map((f, idx) => (
                                        <div
                                            key={idx}
                                            onClick={() => setModalMediaIndex(idx)}
                                            style={{
                                                flexShrink: 0,
                                                width: 72,
                                                height: 72,
                                                borderRadius: 8,
                                                overflow: "hidden",
                                                cursor: "pointer",
                                                border:
                                                    idx === modalMediaIndex
                                                        ? "2px solid #111"
                                                        : "2px solid #e5e7eb",
                                                background: "#f3f4f6",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            {["mp4", "mov", "avi", "webm", "video"].includes(
                                                f.fileType?.toLowerCase()
                                            ) ? (
                                                <div style={{ fontSize: 26, color: "#6b7280" }}>▶</div>
                                            ) : (
                                                <img
                                                    src={f.filePath}
                                                    alt={f.label}
                                                    style={{
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "cover",
                                                    }}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

export default TripMedia;
